import { Injectable } from '@angular/core';
import { PublicPartnerApiService } from './../../../app/@shared/services/publicpartnerapi.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, Action } from '@ngrx/store';
import { concatMap, map, catchError, switchMap, withLatestFrom, filter, tap } from 'rxjs/operators';
import * as LinksActions from './links.actions';
import * as LinksSelectors from './links.selectors';
import { getAuth } from '../auth/auth.selectors';
import { LinkGetResponse, LinksGetResponse } from './../../../app/@shared/models/link.model';
import { globalFailure, noopAction } from '../common.actions';
import { Observable, of } from 'rxjs';
import * as VirtualSwitchActions from './../../../app/@store/virtual_switches/virtual_switches.actions';
import { selectRouteParam } from '../router/router.selector';
import { HttpParams } from '@angular/common/http';
import { RequestOptions } from '@gridscale/gsclient-js';
import { SDNService } from './../../../app/panel/accounts/sdn/sdn.service';
import * as _ from 'lodash';

@Injectable()
export class LinksEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly api: PublicPartnerApiService,
    private readonly SDNService: SDNService) { }

  loadLinks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LinksActions.loadLinks),
      withLatestFrom(this.store.select(getAuth), this.store.select(LinksSelectors.getListConfig)),
      switchMap(([action, auth, config]) => {
        return this.SDNService.getSdnFilter$().pipe(
          map(sdnFilter => {
            const newConfig: any = {
              ...config,
              filter: [
                ..._.get(config, 'filter', []),
                ...sdnFilter
              ] as string[] | undefined
            };
            if (newConfig.filter?.length === 0) {
              delete newConfig.filter;
            }
            return [action, auth, newConfig] as [Action<string>, typeof auth, RequestOptions];
          })
        )
      }),
      concatMap(([action, auth, config]) => {
        return this.api.get<LinksGetResponse>('/partners/' + auth!.partner_uuid + '/links', config as HttpParams).pipe(
          map((result) => {
            return LinksActions.loadLinksSuccess({
              data: result.links,
              meta: result._meta,
              links: result._links
            });
          }),
          catchError(error => of(LinksActions.loadLinksFailure({ error }), globalFailure({ error })))
        );
      })
    )
  );

  loadLinksNext$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LinksActions.loadLinksNext),

      withLatestFrom(this.store.select(LinksSelectors.getLinksLinks)),

      filter(([_, links]) => links !== undefined && links.next !== undefined),
      concatMap(([action, links]) => {
        const url = new URL(links!.next!.href);
        return this.api.get<LinksGetResponse>(url.pathname, {
          page: url.searchParams.get('page') || '0',
          limit: url.searchParams.get('limit') || '10',
          sort: url.searchParams.get('sort') || '',
          fields: url.searchParams.get('fields') ? url.searchParams.get('fields')!.split(',') : [],
        }).pipe(
          map((result) =>
            LinksActions.loadLinksNextSuccess({
              data: result.links || {},
              meta: result._meta,
              links: result._links
            }),
          ),
          catchError(error => of(LinksActions.loadLinksNextFailure({ error }), globalFailure({ error })))
        )
      })
    )
  );

  loadSelectedLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LinksActions.loadSelectedLink),
      withLatestFrom(this.store.select(selectRouteParam('link_uuid'))),
      map(([action, link_uuid]) => LinksActions.loadLink({ link_uuid }))
    )
  );

  loadLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LinksActions.loadLink),
      withLatestFrom(this.store.select(getAuth)),
      concatMap(([action, auth]) => {
        return this.api.get<LinkGetResponse>('/partners/' + auth!.partner_uuid + '/links/' + action.link_uuid).pipe(
          map((result) => {
            return LinksActions.loadLinkSuccess({
              link_uuid: action.link_uuid,
              data: result.link,
            });
          }),
          catchError(error => of(LinksActions.loadLinkFailure({ link_uuid: action.link_uuid, error }), globalFailure({ error })))
        );
      })
    )
  );

  patchLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LinksActions.patchLink),
      withLatestFrom(this.store.select(getAuth)),
      concatMap(([action, auth]) => {
        return this.api.patch<LinkGetResponse>('/partners/' + auth!.partner_uuid + '/links/' + action.link_uuid, action.data as HttpParams).pipe(
          map((result) => {
            return LinksActions.patchLinkSuccess({
              link_uuid: action.link_uuid,
            });
          }),
          catchError(error => of(LinksActions.patchLinkFailure({ link_uuid: action.link_uuid, error }), globalFailure({ error })))
        );
      })
    )
  );

  reloadLinks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        VirtualSwitchActions.patchVirtualSwitchSuccess,
        LinksActions.patchLinkSuccess
      ),
      map(action => LinksActions.loadLinks())
    )
  );

  reloadLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        LinksActions.patchLinkSuccess
      ),
      map(action => LinksActions.loadLink({ link_uuid: action.link_uuid }))
    )
  );

  reloadLinkForeign$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        VirtualSwitchActions.loadVirtualSwitchSuccess
      ),
      map(action => action.data?.link_uuid
        ? LinksActions.loadLink({ link_uuid: action.data.link_uuid })
        : noopAction()
      )

    )
  );
}
